import { NotFoundInPage } from '@/components/NotFoundInPage';
import { Fragment } from 'react';
import type { LogEntry, LogByFilter, LogKindFilter } from '../types';
import { LogEntryPanel } from './LogEntry';
import {
  FilterBar,
  NumberedPager,
  ListEmpty,
  useListUrlState,
  rangeForPreset,
  inRange,
  applySearch,
  paginate,
  type HistoryDatePreset,
} from '@/components/lists';
import { PMS } from '@/features/shared/pms';

const PAGE_SIZE = 50;

const KIND_MATCH: Record<LogKindFilter, LogEntry['kind'] | 'all'> = {
  all: 'all', calls: 'call', portal: 'portal', posted: 'posted', claims: 'claims', appeals: 'appeal', documents: 'document', team: 'your_team',
};

function textOf(e: LogEntry): string {
  return `${e.what} ${e.meta} ${e.result} ${e.by}`;
}

/**
 * ELVA's work — the Log, at scale (00-ds-09 §3, D148). A flat history table:
 * a search, a working date range (presets + a real custom range that replaces
 * the dead "Pick dates"), kind and done-by filters, and a 50-per-page numbered
 * pager. All client-side over the mock log; the server owns this in prod (§8).
 */
export function Log({
  entries,
  selectedId,
  onSelect,
  onClosePanel,
}: {
  entries: LogEntry[];
  selectedId?: string;
  onSelect: (id: string) => void;
  onClosePanel: () => void;
}) {
  const [ls, patch] = useListUrlState({ q: '', kind: 'all', by: 'anyone', range: 'this-month', from: '', to: '', page: '1' });
  const range = ls.range as HistoryDatePreset;
  const kind = ls.kind as LogKindFilter;
  const by = ls.by as LogByFilter;
  const page = Math.max(1, Number(ls.page) || 1);

  const dr = range === 'custom' ? { from: ls.from || null, to: ls.to || null } : rangeForPreset(range);

  let rows = entries;
  if (kind !== 'all') rows = rows.filter((e) => e.kind === KIND_MATCH[kind]);
  if (by !== 'anyone') rows = rows.filter((e) => e.doneBy === by);
  rows = rows.filter((e) => (e.dateIso ? inRange(e.dateIso, dr) : true));
  rows = applySearch(rows, ls.q, textOf);

  const pageRows = paginate(rows, page, PAGE_SIZE);
  const selected = selectedId ? entries.find((e) => e.id === selectedId) ?? null : null;

  return (
    <div className="ew-log">
      <div className="ew-log-main">
        <FilterBar
          search={ls.q}
          onSearch={(v) => { patch({ q: v, page: '1' }); }}
          searchPlaceholder="Search the log — patient, payer, claim or amount"
          dateRange={range}
          onDateRange={(v) => { patch({ range: v, page: '1' }); }}
        >
          <select className="lx-select" aria-label="Kind" value={kind} onChange={(e) => { patch({ kind: e.target.value, page: '1' }); }}>
            <option value="all">Every kind</option>
            <option value="calls">Calls</option>
            <option value="portal">Portal visits</option>
            <option value="posted">Posted in {PMS}</option>
            <option value="claims">Claims sent</option>
            <option value="appeals">Appeals</option>
            <option value="documents">Documents read</option>
            <option value="team">Your team’s actions</option>
          </select>
          <select className="lx-select" aria-label="Done by" value={by} onChange={(e) => { patch({ by: e.target.value, page: '1' }); }}>
            <option value="anyone">Anyone</option>
            <option value="own">ELVA on its own</option>
            <option value="confirmed">ELVA, confirmed by a person</option>
            <option value="person">A person</option>
          </select>
        </FilterBar>

        {range === 'custom' ? (
          <div className="ew-custom-range">
            <label className="ew-meta">From <input type="date" className="ew-field" value={ls.from} onChange={(e) => { patch({ from: e.target.value, page: '1' }); }} /></label>
            <label className="ew-meta">To <input type="date" className="ew-field" value={ls.to} onChange={(e) => { patch({ to: e.target.value, page: '1' }); }} /></label>
          </div>
        ) : null}

        {rows.length === 0 ? (
          <ListEmpty
            {...(ls.q.trim() !== '' ? { query: ls.q } : { message: 'Nothing in this period. Try a longer one, or clear the filters.' })}
            {...(range !== 'all-time' ? { onSearchAllTime: () => { patch({ range: 'all-time', page: '1' }); } } : {})}
          />
        ) : (
          <>
            <div className="ew-loghead">
              <span className="ew-eyebrow">Time</span><span className="ew-eyebrow">What</span><span className="ew-eyebrow">How</span><span className="ew-eyebrow">Result</span><span className="ew-eyebrow">By</span>
            </div>
            {pageRows.map((e, i) => {
              const prev = pageRows[i - 1];
              const showDay = prev?.day !== e.day;
              return (
                <Fragment key={e.id}>
                  {showDay ? <div className="ew-daygrp">{e.day}</div> : null}
                  <div className="ew-row ew-logrow" role="button" tabIndex={0} aria-current={e.id === selectedId ? 'true' : undefined} onClick={() => { onSelect(e.id); }} onKeyDown={(ev) => { if (ev.key === 'Enter') onSelect(e.id); }}>
                    <span className="t">{e.time}</span>
                    <span><span className="ew-kind">{e.kindLabel}</span> {e.what}<div className="ew-meta">{e.meta}</div></span>
                    <span>{e.how}</span>
                    <span>{e.resultBad ? <span className="ew-badge warn">{e.result}</span> : e.result}</span>
                    <span className="ew-meta">{e.by}</span>
                  </div>
                </Fragment>
              );
            })}
            <NumberedPager page={page} pageSize={PAGE_SIZE} total={rows.length} onPage={(p) => { patch({ page: String(p) }); }} />
          </>
        )}
      </div>
      {selected ? <LogEntryPanel entry={selected} onClose={onClosePanel} /> : null}
      {selectedId && !selected ? (
        <aside className="ew-panel" aria-label="Log entry">
          <NotFoundInPage thing="entry" id={selectedId} listLabel="log entries" listTo="/elva-work/log" />
        </aside>
      ) : null}
    </div>
  );
}
